import React, {useState, useRef} from "react";
import styled from "styled-components";
import Box from "../Box/Box";
import Text from "../Text/Text";
import Tag from "../Tag/Tag";
import IconSvg from "../IconSvg/IconSvg";
import SparklesImage from "../assets/sparkles3.svg";
import ListingDetails from "../ListingDetails/ListingDetails";
import ImagePlaceholder from "../../assets/placeholders/snapshot-logo.png";
import { useEffect } from "react";
import { useMedias } from "../hooks/useMedia";
import { breakpoints } from "../../constants";

const Completed = ({completedCount, completed}) => {
  const text = !completed ? 
    `Completed ${completedCount} ${completedCount === 1 ? "time" : "times"}` 
    : "Fully Completed";
  return (
    <>
      <Line />
      <Box flex alignItems="center" gap="6px">
        <IconSvg icon="filled-check-circle" width="18px" color="primary700" />
        <Text extraBold size="14px" lHeight="21px" color="secondary500">{text}</Text>
      </Box>
    </>
  );
};

const ToggleTextIcon = (toggle, iconName, text) => (
  <Box flex pointer spacing="ml-a" size="15px" gap="8px" onClick={toggle}>
    <Text color="accent450" bold={+true}>
      {text}
    </Text>
    <IconSvg color="accent450" icon={iconName} width="10px" />
  </Box>
);

const Listing = ({
  id,
  name,
  description,
  activities,
  reward,
  completed,
  canCompleteMultipleTimes,
  completedCount,
  isAuthenticated,
  image_url,
  onSignUp,
  earned,
  viewYourReceipt,
  contributions_per_user,
  type,
  refresh,
  getDetails,
  ...otherProps
}) => {
  const props = {
    id,
    name,
    description,
    activities,
    reward,
    completed,
    canCompleteMultipleTimes,
    completedCount,
    isAuthenticated,
    image_url,
    onSignUp,
    earned,
    viewYourReceipt,
    contributions_per_user,
    type,
    refresh,
    getDetails,
  };
  const didMount = useRef(false);
  const [showDetails, setShowDetails] = useState(false);
  const {isMobile} = useMedias();
  const toggle = () => {
    setShowDetails(!showDetails);
  };
  const iconName = showDetails ? "up-long" : "down-long";
  const text = showDetails ? "Less detail" : "Progress & details";
  const iconElement = ToggleTextIcon(toggle, iconName, text);

  useEffect(() => {
    if(didMount.current) {
      setShowDetails(true);
    } else {
      didMount.current = true;
    }
  }, [refresh]);

  const rewardElement = (
    <>
      <Box>
        <Text extraBold size="14px" lHeight="21px" color="#3D3D3D" spacing="mb-2">
          {completed ? "Earned" : "Reward"}
        </Text>
        <Box flex alignItems="center" gap="6px">
          <IconSvg icon="logo-gold-circle" width="16px" />
          <Text extraBold lHeight="24px" color="accent550">{completed ? earned : reward} $THRIVE</Text>
        </Box>
      </Box>
      {!completed && completedCount < 1 && (
        <>
          <Line />
          <Text extraBold size="14px" lHeight="21px" color="secondary500">Contribution Opportunity</Text>
        </>
      )}
    </>
  );

  return (
    <Wrapper {...otherProps} relative>
      <Container relative flex bgColor="#fff" >
        <ImageWrapper bgColor="tertiary50" flex alignItems="center" justifyContent="center">
          <StyledImage src={image_url || ImagePlaceholder} placeholder={!image_url} />
        </ImageWrapper>
        <Box spacing={!isMobile && "py-5"} relative grow={1} flex direction="column">
          <Box spacing="pl-6 pr-9+7 pb-5" sm-spacing="p-4+1 pb-5">
            <Text bold spacing="mb-4" size="24px" lHeight="30px" fontFamily="Outfit" color="secondary500">{name}</Text>
            {!!description && <Text spacing="mb-4" color="secondary500" lHeight="24px">{description}</Text>}
            {activities.length > 0 && (
              <Box flex alignItems="center">
                <Text size="14px" lHeight="21px" color="secondary500" bold spacing="mr-2">
                  {activities.length === 1 ? "Activity" : "Activities"}:
                </Text>
                {activities.map((activity, i) => (
                  <StyledTag key={i}>{activity}</StyledTag>
                ))}
              </Box>
            )}
            {isMobile && (
              <Box spacing="mt-5" flex alignItems="center" justifyContent="space-between">
                {rewardElement}
              </Box>
            )}
          </Box>
          <BottomPart
            flex
            alignItems="center"
            spacing="pt-5 pl-6 pr-5 mt-a"
            sm-spacing="px-4+1">
            {!isMobile && rewardElement}
            {(completed || completedCount) > 0 && (
              <Completed
                completed={completed}
                completedCount={completedCount}
                isMobile={isMobile} />
            )}
            {!completed && (canCompleteMultipleTimes || contributions_per_user > 1) && (
              <>
                <Line />
                <IconSvg icon="repeat" width="15px" color="secondary500" spacing="mr-2" />
                <Text
                  extraBold
                  color="secondary500"
                  size="14px"
                  lHeight="21px"
                  width="150px">
                    Can be completed {
                    canCompleteMultipleTimes ? "multiple" : 
                      completedCount > 0 ?
                        `${contributions_per_user - completedCount} more` : contributions_per_user
                  } times
                </Text>
              </>
            )}
            {iconElement}
          </BottomPart>
          {!canCompleteMultipleTimes && completed && <CompletedImage src={SparklesImage} />}
        </Box>
        {!showDetails && !isMobile && (
          <WrapperLines>
            <LineBold />
          </WrapperLines>
        )}
      </Container>
      {showDetails && <ListingDetails {...props} />}
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  z-index: 9;
`;

const Container = styled(Box)`
  border-radius: 12px;
  border: 1px solid ${({theme}) => theme.colors.tertiary200};
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.02);

  @media ${breakpoints.mobile} {
    border: 0;
    border-radius: 0;
    flex-direction: column;
  }
`;

const ImageWrapper = styled(Box)`
  width: 258px;
  min-width: 258px;
  border-radius: 12px 0 0 12px;
  border-right: 1px solid ${({theme}) => theme.colors.tertiary200};

  @media ${breakpoints.mobile} {
    width: auto;
    height: 215px;
    border-radius: 0;
    border: 0;
  }
`;

const StyledTag = styled(Tag)`
  margin: 0 8px 0 0;
  height: 28px;
`;

const BottomPart = styled(Box)`
  border-top: 1px solid ${({theme}) => theme.colors.tertiary200};

  @media ${breakpoints.mobile} {
    height: 84px;
  }
`;

const Line = styled(Box)`
  width: 1px;
  height: 12px;
  background-color: #c4c4c4;
  margin: 0 1rem;

  @media ${breakpoints.mobile} {
    width: 0;
    height: 0;
    margin: 0;
  }
`;

const CompletedImage = styled.img`
  position: absolute;
  top: 29px;
  right: 27px;
  width: 152px;
  height: 120px;
  object-fit: cover;
`;

const WrapperLines = styled.div`
  display: flex;
  flex-direction: column;
  left: 50%;
  transform: translate(-50%, 7px);
  position: absolute;
  width: 100%;
  bottom: 2px;
  z-index: -1;
  border-radius: 50%;
`;

const LineBold = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.tertiary200};
  background: ${({ theme }) => theme.colors.tertiary0};
  height: 20px;
  border-radius: 12px;
  width: 97%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
`;

const StyledImage = styled.img`
  width: ${({placeholder}) => placeholder ? "56px" : "100%"};
  height: ${({placeholder}) => placeholder ? "56px" : "100%"};
  border-radius: 12px 0 0 12px;
  object-fit: cover;
`;

Listing.defaultProps = {
  reward: 0,
};

export default Listing;